import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { IconChipComponent } from '@principle-theorem/ng-principle-shared';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { Patient, Recalls } from '@principle-theorem/principle-core';
import {
  IBrand,
  IPatient,
  IPatientRecall,
  RecallTrack,
} from '@principle-theorem/principle-core/interfaces';
import {
  DAY_MONTH_YEAR_FORMAT,
  Firestore,
  WithRef,
} from '@principle-theorem/shared';
import { switchMap } from 'rxjs/operators';
import { RecallAppointmentComponent } from '../recall-appointment/recall-appointment.component';
import { RecallsService } from '../recalls.service';

@Component({
  selector: 'pr-patient-recalls',
  templateUrl: './patient-recalls.component.html',
  styleUrls: ['./patient-recalls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgCommonModule,
    MatButtonModule,
    RecallAppointmentComponent,
    MatIconModule,
    MatDividerModule,
    IconChipComponent,
  ],
  providers: [RecallsService],
})
export class PatientRecallsComponent {
  readonly dateFormat = DAY_MONTH_YEAR_FORMAT;
  readonly recalls = inject(RecallsService);
  readonly patient = input.required<WithRef<IPatient>>();
  readonly brand: Signal<WithRef<IBrand> | undefined>;
  readonly enabledTracks = computed(() =>
    this.brand()?.settings.secondaryRecalls
      ? [RecallTrack.Primary, RecallTrack.Secondary]
      : [RecallTrack.Primary]
  );

  constructor() {
    const patient$ = toObservable(this.patient);
    const brand$ = patient$.pipe(
      switchMap((patient) => Firestore.getDoc(Patient.brandRef(patient)))
    );
    this.brand = toSignal(brand$);
  }

  trackLabel(track: RecallTrack): string {
    return Recalls.trackLabel(this.brand(), track);
  }

  getRecall(track: RecallTrack): IPatientRecall | undefined {
    return this.patient()[Recalls.trackToField(track)];
  }

  getRecallDueDistance(track: RecallTrack): string | undefined {
    const recall = this.getRecall(track);
    return Recalls.getTimeDistance(recall?.nextRecallDue.dueAt);
  }

  getDefaultPeriodInMonths(track: RecallTrack): number | undefined {
    const settings =
      track === RecallTrack.Primary
        ? this.brand()?.settings?.recalls
        : this.brand()?.settings?.secondaryRecalls;
    return settings?.periodInMonths;
  }
}
